import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useReferredWorkers } from "../hooks/useReferredWorkers";
import referralIllustration from "../assets/referral-illustration.png";

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  const day = d.toLocaleDateString("en-IN", { day: "2-digit" });
  const month = d.toLocaleDateString("en-IN", { month: "short" }).toLowerCase();
  return `${day} ${month} ${d.getFullYear()}`;
};

const MyReferralsContent = ({ token, userId, workerType }) => {
  const navigate = useNavigate();
  const {
    visibleReferrals,
    allReferrals,
    totalReferrals: apiTotal,
    hasMore,
    loadMore,
    initialLoading,
    loadingMore,
    error,
  } = useReferredWorkers({ token, userId, workerType });

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

    return () => observerRef.current?.disconnect();
  }, [loadMore, loadingMore, hasMore]);

  const source = allReferrals || visibleReferrals;
  const totalReferrals = apiTotal ?? source.length;
  const earnedCount = source.filter((r) => Number(r.earning) > 0).length;

  return (
    <div className="min-h-screen bg-[#fafafc] pt-20 pb-8 text-gray-900 font-sans">
      <div className="max-w-md mx-auto px-3.5">
        {/* Header */}
        <div className="relative flex items-center justify-center mb-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="absolute left-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
          >
            <ChevronLeft />
          </button>
          <h1 className="text-lg font-bold text-gray-900">My Referrals</h1>
        </div>

        {/* Summary + illustration */}
        {!initialLoading && !error && (
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Total Referrals</p>
              <p className="text-3xl font-bold text-green-700 leading-tight">
                {totalReferrals}
              </p>
              <p className="text-xs text-gray-500 mt-1.5">
                Total Earned Referrals :{" "}
                <span className="text-base font-bold text-green-700">{earnedCount}</span>
                <span className="text-gray-800 font-semibold text-xs">/{totalReferrals}</span>
              </p>
            </div>
            <img
              src={referralIllustration}
              alt="Referrals"
              className="w-24 h-auto object-contain shrink-0 md:hidden"
            />
          </div>
        )}

        {initialLoading && (
          <div className="flex justify-center py-6">
            <div className="w-7 h-7 border-[3px] border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        )}

        {error && <p className="text-center text-red-600 text-xs my-4">{error}</p>}

        {!initialLoading && !error && visibleReferrals.length === 0 && (
          <p className="text-center text-gray-400 text-xs my-8">No referrals yet.</p>
        )}

        {/* Cards */}
        {!initialLoading &&
          !error &&
          visibleReferrals.map((ref, index) => {
            const earned = Number(ref.earning) > 0;
            return (
              <div
                key={ref.workerId || index}
                className="bg-white rounded-xl border border-gray-100 shadow-sm px-3.5 py-3 mb-2.5 flex justify-between items-start"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <span className="text-xs text-gray-500 min-w-[16px] pt-0.5">{index + 1}.</span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 mb-1 truncate">
                      {ref.name || "Unknown"}
                    </p>
                    <p className="text-xs text-gray-600">
                      {ref.phone ? `+91 ${ref.phone}` : "—"}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <p className="text-[11px] text-gray-500 mb-1.5 pt-0.5">
                    Joined on : {formatDate(ref.onBoardingDate)}
                  </p>
                  <p
                    className={`text-xs font-semibold ${
                      earned ? "text-green-600" : "text-[#d4a62a]"
                    }`}
                  >
                    {earned ? `₹${ref.earning} Earned` : "Not Earned"}
                  </p>
                </div>
              </div>
            );
          })}

        {!initialLoading && (
          <div ref={sentinelRef} className="min-h-[20px]">
            {loadingMore && (
              <div className="flex justify-center py-3">
                <div className="w-5 h-5 border-2 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
              </div>
            )}
            {!hasMore && visibleReferrals.length > 0 && (
              <p className="text-center text-gray-400 text-[11px] my-2">
                You've seen all referrals
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReferralsContent;